<template>
  <div class="emoji-picker">
    <button
      ref="triggerRef"
      class="emoji-picker__trigger"
      type="button"
      title="Add emoji"
      aria-label="Add emoji"
      :aria-expanded="isOpen"
      @click="togglePicker"
    >
      <v-icon size="20">mdi-emoticon-outline</v-icon>
    </button>

    <Teleport to="body">
      <Transition name="emoji-picker-pop">
        <div
          v-if="isOpen"
          ref="pickerRef"
          class="emoji-picker__popup"
          :class="`v-theme--${currentTheme}`"
          :style="popupStyle"
          role="dialog"
          aria-label="Choose an emoji"
          @click.stop
        >
          <!-- Search -->
          <div class="emoji-picker__search">
            <v-icon size="16" class="emoji-picker__search-icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search emojis..."
              class="emoji-picker__search-input"
              @input="handleSearch"
            />
          </div>

          <!-- Category tabs -->
          <div class="emoji-picker__categories">
            <button
              v-for="category in categories"
              :key="category.id"
              class="emoji-picker__category-btn"
              :class="{ 'emoji-picker__category-btn--active': activeCategory === category.id }"
              :title="category.name"
              @click="activeCategory = category.id"
            >
              {{ category.icon }}
            </button>
          </div>

          <!-- Emoji grid -->
          <div class="emoji-picker__grid-container">
            <div class="emoji-picker__grid">
              <button
                v-for="emoji in filteredEmojis"
                :key="emoji.emoji"
                class="emoji-picker__emoji"
                :title="emoji.name"
                @click="selectEmoji(emoji.emoji)"
              >
                {{ emoji.emoji }}
              </button>
            </div>
            <div v-if="filteredEmojis.length === 0" class="emoji-picker__empty">
              No emojis found
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type CSSProperties } from "vue";
import { useUiStore } from "@/stores/ui";

interface Emoji {
  emoji: string;
  name: string;
  category: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
}

const emit = defineEmits<{
  select: [emoji: string];
}>();

const uiStore = useUiStore();
const currentTheme = computed(() => uiStore.effectiveTheme);

// State
const isOpen = ref(false);
const searchQuery = ref("");
const activeCategory = ref("smileys");
const triggerRef = ref<HTMLButtonElement | null>(null);
const pickerRef = ref<HTMLDivElement | null>(null);
const triggerRect = ref<DOMRect | null>(null);

// Categories
const categories: Category[] = [
  { id: "smileys", name: "Smileys & Emotion", icon: "😀" },
  { id: "people", name: "People & Body", icon: "👋" },
  { id: "animals", name: "Animals & Nature", icon: "🐱" },
  { id: "food", name: "Food & Drink", icon: "🍕" },
  { id: "activities", name: "Activities", icon: "⚽" },
  { id: "travel", name: "Travel & Places", icon: "🚗" },
  { id: "objects", name: "Objects", icon: "💡" },
  { id: "symbols", name: "Symbols", icon: "❤️" },
];

// Emoji data organized by category
const emojiData: Record<string, Emoji[]> = {
  smileys: [
    { emoji: "😀", name: "grinning face", category: "smileys" },
    { emoji: "😃", name: "grinning face with big eyes", category: "smileys" },
    { emoji: "😄", name: "grinning face with smiling eyes", category: "smileys" },
    { emoji: "😁", name: "beaming face with smiling eyes", category: "smileys" },
    { emoji: "😆", name: "grinning squinting face", category: "smileys" },
    { emoji: "😅", name: "grinning face with sweat", category: "smileys" },
    { emoji: "🤣", name: "rolling on the floor laughing", category: "smileys" },
    { emoji: "😂", name: "face with tears of joy", category: "smileys" },
    { emoji: "🙂", name: "slightly smiling face", category: "smileys" },
    { emoji: "🙃", name: "upside-down face", category: "smileys" },
    { emoji: "😉", name: "winking face", category: "smileys" },
    { emoji: "😊", name: "smiling face with smiling eyes", category: "smileys" },
    { emoji: "😇", name: "smiling face with halo", category: "smileys" },
    { emoji: "🥰", name: "smiling face with hearts", category: "smileys" },
    { emoji: "😍", name: "smiling face with heart-eyes", category: "smileys" },
    { emoji: "🤩", name: "star-struck", category: "smileys" },
    { emoji: "😘", name: "face blowing a kiss", category: "smileys" },
    { emoji: "😗", name: "kissing face", category: "smileys" },
    { emoji: "😚", name: "kissing face with closed eyes", category: "smileys" },
    { emoji: "😙", name: "kissing face with smiling eyes", category: "smileys" },
    { emoji: "🥲", name: "smiling face with tear", category: "smileys" },
    { emoji: "😋", name: "face savoring food", category: "smileys" },
    { emoji: "😛", name: "face with tongue", category: "smileys" },
    { emoji: "😜", name: "winking face with tongue", category: "smileys" },
    { emoji: "🤪", name: "zany face", category: "smileys" },
    { emoji: "😝", name: "squinting face with tongue", category: "smileys" },
    { emoji: "🤑", name: "money-mouth face", category: "smileys" },
    { emoji: "🤗", name: "hugging face", category: "smileys" },
    { emoji: "🤭", name: "face with hand over mouth", category: "smileys" },
    { emoji: "🤫", name: "shushing face", category: "smileys" },
    { emoji: "🤔", name: "thinking face", category: "smileys" },
    { emoji: "🤐", name: "zipper-mouth face", category: "smileys" },
    { emoji: "🤨", name: "face with raised eyebrow", category: "smileys" },
    { emoji: "😐", name: "neutral face", category: "smileys" },
    { emoji: "😑", name: "expressionless face", category: "smileys" },
    { emoji: "😶", name: "face without mouth", category: "smileys" },
    { emoji: "😏", name: "smirking face", category: "smileys" },
    { emoji: "😒", name: "unamused face", category: "smileys" },
    { emoji: "🙄", name: "face with rolling eyes", category: "smileys" },
    { emoji: "😬", name: "grimacing face", category: "smileys" },
    { emoji: "🤥", name: "lying face", category: "smileys" },
    { emoji: "😌", name: "relieved face", category: "smileys" },
    { emoji: "😔", name: "pensive face", category: "smileys" },
    { emoji: "😪", name: "sleepy face", category: "smileys" },
    { emoji: "🤤", name: "drooling face", category: "smileys" },
    { emoji: "😴", name: "sleeping face", category: "smileys" },
    { emoji: "😷", name: "face with medical mask", category: "smileys" },
    { emoji: "🤒", name: "face with thermometer", category: "smileys" },
    { emoji: "🤕", name: "face with head-bandage", category: "smileys" },
    { emoji: "🤢", name: "nauseated face", category: "smileys" },
    { emoji: "🤮", name: "face vomiting", category: "smileys" },
    { emoji: "🤧", name: "sneezing face", category: "smileys" },
    { emoji: "🥵", name: "hot face", category: "smileys" },
    { emoji: "🥶", name: "cold face", category: "smileys" },
    { emoji: "🥴", name: "woozy face", category: "smileys" },
    { emoji: "😵", name: "dizzy face", category: "smileys" },
    { emoji: "🤯", name: "exploding head", category: "smileys" },
    { emoji: "🤠", name: "cowboy hat face", category: "smileys" },
    { emoji: "🥳", name: "partying face", category: "smileys" },
    { emoji: "🥸", name: "disguised face", category: "smileys" },
    { emoji: "😎", name: "smiling face with sunglasses", category: "smileys" },
    { emoji: "🤓", name: "nerd face", category: "smileys" },
    { emoji: "🧐", name: "face with monocle", category: "smileys" },
    { emoji: "😕", name: "confused face", category: "smileys" },
    { emoji: "😟", name: "worried face", category: "smileys" },
    { emoji: "🙁", name: "slightly frowning face", category: "smileys" },
    { emoji: "😮", name: "face with open mouth", category: "smileys" },
    { emoji: "😯", name: "hushed face", category: "smileys" },
    { emoji: "😲", name: "astonished face", category: "smileys" },
    { emoji: "😳", name: "flushed face", category: "smileys" },
    { emoji: "🥺", name: "pleading face", category: "smileys" },
    { emoji: "😦", name: "frowning face with open mouth", category: "smileys" },
    { emoji: "😧", name: "anguished face", category: "smileys" },
    { emoji: "😨", name: "fearful face", category: "smileys" },
    { emoji: "😰", name: "anxious face with sweat", category: "smileys" },
    { emoji: "😥", name: "sad but relieved face", category: "smileys" },
    { emoji: "😢", name: "crying face", category: "smileys" },
    { emoji: "😭", name: "loudly crying face", category: "smileys" },
    { emoji: "😱", name: "face screaming in fear", category: "smileys" },
    { emoji: "😖", name: "confounded face", category: "smileys" },
    { emoji: "😣", name: "persevering face", category: "smileys" },
    { emoji: "😞", name: "disappointed face", category: "smileys" },
    { emoji: "😓", name: "downcast face with sweat", category: "smileys" },
    { emoji: "😩", name: "weary face", category: "smileys" },
    { emoji: "😫", name: "tired face", category: "smileys" },
    { emoji: "🥱", name: "yawning face", category: "smileys" },
    { emoji: "😤", name: "face with steam from nose", category: "smileys" },
    { emoji: "😡", name: "pouting face", category: "smileys" },
    { emoji: "😠", name: "angry face", category: "smileys" },
    { emoji: "🤬", name: "face with symbols on mouth", category: "smileys" },
    { emoji: "😈", name: "smiling face with horns", category: "smileys" },
    { emoji: "👿", name: "angry face with horns", category: "smileys" },
    { emoji: "💀", name: "skull", category: "smileys" },
    { emoji: "☠️", name: "skull and crossbones", category: "smileys" },
    { emoji: "💩", name: "pile of poo", category: "smileys" },
    { emoji: "🤡", name: "clown face", category: "smileys" },
    { emoji: "👹", name: "ogre", category: "smileys" },
    { emoji: "👺", name: "goblin", category: "smileys" },
    { emoji: "👻", name: "ghost", category: "smileys" },
    { emoji: "👽", name: "alien", category: "smileys" },
    { emoji: "👾", name: "alien monster", category: "smileys" },
    { emoji: "🤖", name: "robot", category: "smileys" },
  ],
  people: [
    { emoji: "👋", name: "waving hand", category: "people" },
    { emoji: "🤚", name: "raised back of hand", category: "people" },
    { emoji: "🖐️", name: "hand with fingers splayed", category: "people" },
    { emoji: "✋", name: "raised hand", category: "people" },
    { emoji: "🖖", name: "vulcan salute", category: "people" },
    { emoji: "👌", name: "OK hand", category: "people" },
    { emoji: "🤌", name: "pinched fingers", category: "people" },
    { emoji: "🤏", name: "pinching hand", category: "people" },
    { emoji: "✌️", name: "victory hand", category: "people" },
    { emoji: "🤞", name: "crossed fingers", category: "people" },
    { emoji: "🤟", name: "love-you gesture", category: "people" },
    { emoji: "🤘", name: "sign of the horns", category: "people" },
    { emoji: "🤙", name: "call me hand", category: "people" },
    { emoji: "👈", name: "backhand index pointing left", category: "people" },
    { emoji: "👉", name: "backhand index pointing right", category: "people" },
    { emoji: "👆", name: "backhand index pointing up", category: "people" },
    { emoji: "🖕", name: "middle finger", category: "people" },
    { emoji: "👇", name: "backhand index pointing down", category: "people" },
    { emoji: "☝️", name: "index pointing up", category: "people" },
    { emoji: "👍", name: "thumbs up", category: "people" },
    { emoji: "👎", name: "thumbs down", category: "people" },
    { emoji: "✊", name: "raised fist", category: "people" },
    { emoji: "👊", name: "oncoming fist", category: "people" },
    { emoji: "🤛", name: "left-facing fist", category: "people" },
    { emoji: "🤜", name: "right-facing fist", category: "people" },
    { emoji: "👏", name: "clapping hands", category: "people" },
    { emoji: "🙌", name: "raising hands", category: "people" },
    { emoji: "👐", name: "open hands", category: "people" },
    { emoji: "🤲", name: "palms up together", category: "people" },
    { emoji: "🤝", name: "handshake", category: "people" },
    { emoji: "🙏", name: "folded hands", category: "people" },
    { emoji: "✍️", name: "writing hand", category: "people" },
    { emoji: "💅", name: "nail polish", category: "people" },
    { emoji: "🤳", name: "selfie", category: "people" },
    { emoji: "💪", name: "flexed biceps", category: "people" },
    { emoji: "🦾", name: "mechanical arm", category: "people" },
    { emoji: "🦿", name: "mechanical leg", category: "people" },
    { emoji: "🦵", name: "leg", category: "people" },
    { emoji: "🦶", name: "foot", category: "people" },
    { emoji: "👂", name: "ear", category: "people" },
    { emoji: "🦻", name: "ear with hearing aid", category: "people" },
    { emoji: "👃", name: "nose", category: "people" },
    { emoji: "🧠", name: "brain", category: "people" },
    { emoji: "👀", name: "eyes", category: "people" },
    { emoji: "👁️", name: "eye", category: "people" },
    { emoji: "👅", name: "tongue", category: "people" },
    { emoji: "👄", name: "mouth", category: "people" },
  ],
  animals: [
    { emoji: "🐱", name: "cat face", category: "animals" },
    { emoji: "🐶", name: "dog face", category: "animals" },
    { emoji: "🐭", name: "mouse face", category: "animals" },
    { emoji: "🐹", name: "hamster", category: "animals" },
    { emoji: "🐰", name: "rabbit face", category: "animals" },
    { emoji: "🦊", name: "fox", category: "animals" },
    { emoji: "🐻", name: "bear", category: "animals" },
    { emoji: "🐼", name: "panda", category: "animals" },
    { emoji: "🐨", name: "koala", category: "animals" },
    { emoji: "🐯", name: "tiger face", category: "animals" },
    { emoji: "🦁", name: "lion", category: "animals" },
    { emoji: "🐮", name: "cow face", category: "animals" },
    { emoji: "🐷", name: "pig face", category: "animals" },
    { emoji: "🐸", name: "frog", category: "animals" },
    { emoji: "🐵", name: "monkey face", category: "animals" },
    { emoji: "🐔", name: "chicken", category: "animals" },
    { emoji: "🐧", name: "penguin", category: "animals" },
    { emoji: "🐦", name: "bird", category: "animals" },
    { emoji: "🐤", name: "baby chick", category: "animals" },
    { emoji: "🦆", name: "duck", category: "animals" },
    { emoji: "🦅", name: "eagle", category: "animals" },
    { emoji: "🦉", name: "owl", category: "animals" },
    { emoji: "🦇", name: "bat", category: "animals" },
    { emoji: "🐺", name: "wolf", category: "animals" },
    { emoji: "🐗", name: "boar", category: "animals" },
    { emoji: "🐴", name: "horse face", category: "animals" },
    { emoji: "🦄", name: "unicorn", category: "animals" },
    { emoji: "🐝", name: "honeybee", category: "animals" },
    { emoji: "🐛", name: "bug", category: "animals" },
    { emoji: "🦋", name: "butterfly", category: "animals" },
    { emoji: "🐌", name: "snail", category: "animals" },
    { emoji: "🐞", name: "lady beetle", category: "animals" },
    { emoji: "🐜", name: "ant", category: "animals" },
    { emoji: "🦟", name: "mosquito", category: "animals" },
    { emoji: "🦗", name: "cricket", category: "animals" },
    { emoji: "🐢", name: "turtle", category: "animals" },
    { emoji: "🐍", name: "snake", category: "animals" },
    { emoji: "🦎", name: "lizard", category: "animals" },
    { emoji: "🦖", name: "T-Rex", category: "animals" },
    { emoji: "🦕", name: "sauropod", category: "animals" },
    { emoji: "🐙", name: "octopus", category: "animals" },
    { emoji: "🦑", name: "squid", category: "animals" },
    { emoji: "🦐", name: "shrimp", category: "animals" },
    { emoji: "🦞", name: "lobster", category: "animals" },
    { emoji: "🦀", name: "crab", category: "animals" },
    { emoji: "🐡", name: "blowfish", category: "animals" },
    { emoji: "🐠", name: "tropical fish", category: "animals" },
    { emoji: "🐟", name: "fish", category: "animals" },
    { emoji: "🐬", name: "dolphin", category: "animals" },
    { emoji: "🐳", name: "spouting whale", category: "animals" },
    { emoji: "🐋", name: "whale", category: "animals" },
    { emoji: "🦈", name: "shark", category: "animals" },
    { emoji: "🐊", name: "crocodile", category: "animals" },
    { emoji: "🐅", name: "tiger", category: "animals" },
    { emoji: "🐆", name: "leopard", category: "animals" },
    { emoji: "🦓", name: "zebra", category: "animals" },
    { emoji: "🦍", name: "gorilla", category: "animals" },
    { emoji: "🦧", name: "orangutan", category: "animals" },
    { emoji: "🐘", name: "elephant", category: "animals" },
    { emoji: "🦛", name: "hippopotamus", category: "animals" },
    { emoji: "🦏", name: "rhinoceros", category: "animals" },
    { emoji: "🐪", name: "camel", category: "animals" },
    { emoji: "🐫", name: "two-hump camel", category: "animals" },
    { emoji: "🦒", name: "giraffe", category: "animals" },
    { emoji: "🦘", name: "kangaroo", category: "animals" },
    { emoji: "🐃", name: "water buffalo", category: "animals" },
    { emoji: "🐂", name: "ox", category: "animals" },
    { emoji: "🐄", name: "cow", category: "animals" },
    { emoji: "🐎", name: "horse", category: "animals" },
    { emoji: "🐖", name: "pig", category: "animals" },
    { emoji: "🐏", name: "ram", category: "animals" },
    { emoji: "🐑", name: "ewe", category: "animals" },
    { emoji: "🦙", name: "llama", category: "animals" },
    { emoji: "🐐", name: "goat", category: "animals" },
    { emoji: "🦌", name: "deer", category: "animals" },
    { emoji: "🐕", name: "dog", category: "animals" },
    { emoji: "🐩", name: "poodle", category: "animals" },
    { emoji: "🦮", name: "guide dog", category: "animals" },
    { emoji: "🐈", name: "cat", category: "animals" },
    { emoji: "🐓", name: "rooster", category: "animals" },
    { emoji: "🦃", name: "turkey", category: "animals" },
    { emoji: "🦚", name: "peacock", category: "animals" },
    { emoji: "🦜", name: "parrot", category: "animals" },
    { emoji: "🦢", name: "swan", category: "animals" },
    { emoji: "🦩", name: "flamingo", category: "animals" },
    { emoji: "🐇", name: "rabbit", category: "animals" },
    { emoji: "🦝", name: "raccoon", category: "animals" },
    { emoji: "🦨", name: "skunk", category: "animals" },
    { emoji: "🦡", name: "badger", category: "animals" },
    { emoji: "🦫", name: "beaver", category: "animals" },
    { emoji: "🦦", name: "otter", category: "animals" },
    { emoji: "🦥", name: "sloth", category: "animals" },
    { emoji: "🐁", name: "mouse", category: "animals" },
    { emoji: "🐀", name: "rat", category: "animals" },
    { emoji: "🐿️", name: "chipmunk", category: "animals" },
    { emoji: "🦔", name: "hedgehog", category: "animals" },
  ],
  food: [
    { emoji: "🍕", name: "pizza", category: "food" },
    { emoji: "🍔", name: "hamburger", category: "food" },
    { emoji: "🍟", name: "french fries", category: "food" },
    { emoji: "🌭", name: "hot dog", category: "food" },
    { emoji: "🍿", name: "popcorn", category: "food" },
    { emoji: "🧂", name: "salt", category: "food" },
    { emoji: "🥓", name: "bacon", category: "food" },
    { emoji: "🥚", name: "egg", category: "food" },
    { emoji: "🍳", name: "cooking", category: "food" },
    { emoji: "🧇", name: "waffle", category: "food" },
    { emoji: "🥞", name: "pancakes", category: "food" },
    { emoji: "🧈", name: "butter", category: "food" },
    { emoji: "🍞", name: "bread", category: "food" },
    { emoji: "🥐", name: "croissant", category: "food" },
    { emoji: "🥖", name: "baguette bread", category: "food" },
    { emoji: "🥨", name: "pretzel", category: "food" },
    { emoji: "🧀", name: "cheese wedge", category: "food" },
    { emoji: "🥗", name: "green salad", category: "food" },
    { emoji: "🥙", name: "stuffed flatbread", category: "food" },
    { emoji: "🥪", name: "sandwich", category: "food" },
    { emoji: "🌮", name: "taco", category: "food" },
    { emoji: "🌯", name: "burrito", category: "food" },
    { emoji: "🫔", name: "tamale", category: "food" },
    { emoji: "🥫", name: "canned food", category: "food" },
    { emoji: "🍝", name: "spaghetti", category: "food" },
    { emoji: "🍜", name: "steaming bowl", category: "food" },
    { emoji: "🍲", name: "pot of food", category: "food" },
    { emoji: "🍛", name: "curry rice", category: "food" },
    { emoji: "🍣", name: "sushi", category: "food" },
    { emoji: "🍱", name: "bento box", category: "food" },
    { emoji: "🥟", name: "dumpling", category: "food" },
    { emoji: "🍤", name: "fried shrimp", category: "food" },
    { emoji: "🍙", name: "rice ball", category: "food" },
    { emoji: "🍚", name: "cooked rice", category: "food" },
    { emoji: "🍘", name: "rice cracker", category: "food" },
    { emoji: "🍥", name: "fish cake with swirl", category: "food" },
    { emoji: "🥠", name: "fortune cookie", category: "food" },
    { emoji: "🥡", name: "takeout box", category: "food" },
    { emoji: "🍦", name: "soft ice cream", category: "food" },
    { emoji: "🍧", name: "shaved ice", category: "food" },
    { emoji: "🍨", name: "ice cream", category: "food" },
    { emoji: "🍩", name: "doughnut", category: "food" },
    { emoji: "🍪", name: "cookie", category: "food" },
    { emoji: "🎂", name: "birthday cake", category: "food" },
    { emoji: "🍰", name: "shortcake", category: "food" },
    { emoji: "🧁", name: "cupcake", category: "food" },
    { emoji: "🥧", name: "pie", category: "food" },
    { emoji: "🍫", name: "chocolate bar", category: "food" },
    { emoji: "🍬", name: "candy", category: "food" },
    { emoji: "🍭", name: "lollipop", category: "food" },
    { emoji: "🍮", name: "custard", category: "food" },
    { emoji: "🍯", name: "honey pot", category: "food" },
    { emoji: "🍼", name: "baby bottle", category: "food" },
    { emoji: "🥛", name: "glass of milk", category: "food" },
    { emoji: "☕", name: "hot beverage", category: "food" },
    { emoji: "🫖", name: "teapot", category: "food" },
    { emoji: "🍵", name: "teacup without handle", category: "food" },
    { emoji: "🍶", name: "sake", category: "food" },
    { emoji: "🍾", name: "bottle with popping cork", category: "food" },
    { emoji: "🍷", name: "wine glass", category: "food" },
    { emoji: "🍸", name: "cocktail glass", category: "food" },
    { emoji: "🍹", name: "tropical drink", category: "food" },
    { emoji: "🍺", name: "beer mug", category: "food" },
    { emoji: "🍻", name: "clinking beer mugs", category: "food" },
    { emoji: "🥂", name: "clinking glasses", category: "food" },
    { emoji: "🥃", name: "tumbler glass", category: "food" },
    { emoji: "🥤", name: "cup with straw", category: "food" },
    { emoji: "🧋", name: "bubble tea", category: "food" },
    { emoji: "🧃", name: "beverage box", category: "food" },
    { emoji: "🧉", name: "mate", category: "food" },
    { emoji: "🧊", name: "ice", category: "food" },
    { emoji: "🍇", name: "grapes", category: "food" },
    { emoji: "🍈", name: "melon", category: "food" },
    { emoji: "🍉", name: "watermelon", category: "food" },
    { emoji: "🍊", name: "tangerine", category: "food" },
    { emoji: "🍋", name: "lemon", category: "food" },
    { emoji: "🍌", name: "banana", category: "food" },
    { emoji: "🍍", name: "pineapple", category: "food" },
    { emoji: "🥭", name: "mango", category: "food" },
    { emoji: "🍎", name: "red apple", category: "food" },
    { emoji: "🍏", name: "green apple", category: "food" },
    { emoji: "🍐", name: "pear", category: "food" },
    { emoji: "🍑", name: "peach", category: "food" },
    { emoji: "🍒", name: "cherries", category: "food" },
    { emoji: "🍓", name: "strawberry", category: "food" },
    { emoji: "🫐", name: "blueberries", category: "food" },
    { emoji: "🥝", name: "kiwi fruit", category: "food" },
    { emoji: "🍅", name: "tomato", category: "food" },
    { emoji: "🫒", name: "olive", category: "food" },
    { emoji: "🥥", name: "coconut", category: "food" },
    { emoji: "🥑", name: "avocado", category: "food" },
    { emoji: "🍆", name: "eggplant", category: "food" },
    { emoji: "🥔", name: "potato", category: "food" },
    { emoji: "🥕", name: "carrot", category: "food" },
    { emoji: "🌽", name: "ear of corn", category: "food" },
    { emoji: "🌶️", name: "hot pepper", category: "food" },
    { emoji: "🫑", name: "bell pepper", category: "food" },
    { emoji: "🥒", name: "cucumber", category: "food" },
    { emoji: "🥬", name: "leafy green", category: "food" },
    { emoji: "🥦", name: "broccoli", category: "food" },
    { emoji: "🧄", name: "garlic", category: "food" },
    { emoji: "🧅", name: "onion", category: "food" },
    { emoji: "🍄", name: "mushroom", category: "food" },
    { emoji: "🥜", name: "peanuts", category: "food" },
    { emoji: "🌰", name: "chestnut", category: "food" },
  ],
  activities: [
    { emoji: "⚽", name: "soccer ball", category: "activities" },
    { emoji: "🏀", name: "basketball", category: "activities" },
    { emoji: "🏈", name: "american football", category: "activities" },
    { emoji: "⚾", name: "baseball", category: "activities" },
    { emoji: "🥎", name: "softball", category: "activities" },
    { emoji: "🎾", name: "tennis", category: "activities" },
    { emoji: "🏐", name: "volleyball", category: "activities" },
    { emoji: "🏉", name: "rugby football", category: "activities" },
    { emoji: "🥏", name: "flying disc", category: "activities" },
    { emoji: "🎱", name: "pool 8 ball", category: "activities" },
    { emoji: "🪀", name: "yo-yo", category: "activities" },
    { emoji: "🏓", name: "ping pong", category: "activities" },
    { emoji: "🏸", name: "badminton", category: "activities" },
    { emoji: "🏒", name: "ice hockey", category: "activities" },
    { emoji: "🏑", name: "field hockey", category: "activities" },
    { emoji: "🥍", name: "lacrosse", category: "activities" },
    { emoji: "🏏", name: "cricket game", category: "activities" },
    { emoji: "🪃", name: "boomerang", category: "activities" },
    { emoji: "🥅", name: "goal net", category: "activities" },
    { emoji: "⛳", name: "flag in hole", category: "activities" },
    { emoji: "🪁", name: "kite", category: "activities" },
    { emoji: "🏹", name: "bow and arrow", category: "activities" },
    { emoji: "🎣", name: "fishing pole", category: "activities" },
    { emoji: "🤿", name: "diving mask", category: "activities" },
    { emoji: "🥊", name: "boxing glove", category: "activities" },
    { emoji: "🥋", name: "martial arts uniform", category: "activities" },
    { emoji: "🎽", name: "running shirt", category: "activities" },
    { emoji: "🛹", name: "skateboard", category: "activities" },
    { emoji: "🛼", name: "roller skate", category: "activities" },
    { emoji: "🛷", name: "sled", category: "activities" },
    { emoji: "⛸️", name: "ice skate", category: "activities" },
    { emoji: "🥌", name: "curling stone", category: "activities" },
    { emoji: "🎿", name: "skis", category: "activities" },
    { emoji: "⛷️", name: "skier", category: "activities" },
    { emoji: "🏂", name: "snowboarder", category: "activities" },
    { emoji: "🪂", name: "parachute", category: "activities" },
    { emoji: "🏋️", name: "person lifting weights", category: "activities" },
    { emoji: "🤸", name: "person cartwheeling", category: "activities" },
    { emoji: "🤼", name: "people wrestling", category: "activities" },
    { emoji: "🤽", name: "person playing water polo", category: "activities" },
    { emoji: "🤾", name: "person playing handball", category: "activities" },
    { emoji: "🏌️", name: "person golfing", category: "activities" },
    { emoji: "🏇", name: "horse racing", category: "activities" },
    { emoji: "🧘", name: "person in lotus position", category: "activities" },
    { emoji: "🏄", name: "person surfing", category: "activities" },
    { emoji: "🏊", name: "person swimming", category: "activities" },
    { emoji: "🚣", name: "person rowing boat", category: "activities" },
    { emoji: "🧗", name: "person climbing", category: "activities" },
    { emoji: "🚵", name: "person mountain biking", category: "activities" },
    { emoji: "🚴", name: "person biking", category: "activities" },
    { emoji: "🏆", name: "trophy", category: "activities" },
    { emoji: "🥇", name: "1st place medal", category: "activities" },
    { emoji: "🥈", name: "2nd place medal", category: "activities" },
    { emoji: "🥉", name: "3rd place medal", category: "activities" },
    { emoji: "🏅", name: "sports medal", category: "activities" },
    { emoji: "🎖️", name: "military medal", category: "activities" },
    { emoji: "🎗️", name: "reminder ribbon", category: "activities" },
    { emoji: "🎪", name: "circus tent", category: "activities" },
    { emoji: "🎭", name: "performing arts", category: "activities" },
    { emoji: "🎨", name: "artist palette", category: "activities" },
    { emoji: "🎬", name: "clapper board", category: "activities" },
    { emoji: "🎤", name: "microphone", category: "activities" },
    { emoji: "🎧", name: "headphone", category: "activities" },
    { emoji: "🎼", name: "musical score", category: "activities" },
    { emoji: "🎹", name: "musical keyboard", category: "activities" },
    { emoji: "🥁", name: "drum", category: "activities" },
    { emoji: "🪘", name: "long drum", category: "activities" },
    { emoji: "🎷", name: "saxophone", category: "activities" },
    { emoji: "🎺", name: "trumpet", category: "activities" },
    { emoji: "🎸", name: "guitar", category: "activities" },
    { emoji: "🪕", name: "banjo", category: "activities" },
    { emoji: "🎻", name: "violin", category: "activities" },
    { emoji: "🎲", name: "game die", category: "activities" },
    { emoji: "♟️", name: "chess pawn", category: "activities" },
    { emoji: "🎯", name: "direct hit", category: "activities" },
    { emoji: "🎳", name: "bowling", category: "activities" },
    { emoji: "🎮", name: "video game", category: "activities" },
    { emoji: "🎰", name: "slot machine", category: "activities" },
    { emoji: "🧩", name: "puzzle piece", category: "activities" },
  ],
  travel: [
    { emoji: "🚗", name: "automobile", category: "travel" },
    { emoji: "🚕", name: "taxi", category: "travel" },
    { emoji: "🚙", name: "sport utility vehicle", category: "travel" },
    { emoji: "🚌", name: "bus", category: "travel" },
    { emoji: "🚎", name: "trolleybus", category: "travel" },
    { emoji: "🏎️", name: "racing car", category: "travel" },
    { emoji: "🚓", name: "police car", category: "travel" },
    { emoji: "🚑", name: "ambulance", category: "travel" },
    { emoji: "🚒", name: "fire engine", category: "travel" },
    { emoji: "🚐", name: "minibus", category: "travel" },
    { emoji: "🛻", name: "pickup truck", category: "travel" },
    { emoji: "🚚", name: "delivery truck", category: "travel" },
    { emoji: "🚛", name: "articulated lorry", category: "travel" },
    { emoji: "🚜", name: "tractor", category: "travel" },
    { emoji: "🏍️", name: "motorcycle", category: "travel" },
    { emoji: "🛵", name: "motor scooter", category: "travel" },
    { emoji: "🚲", name: "bicycle", category: "travel" },
    { emoji: "🛴", name: "kick scooter", category: "travel" },
    { emoji: "🚏", name: "bus stop", category: "travel" },
    { emoji: "🛣️", name: "motorway", category: "travel" },
    { emoji: "🛤️", name: "railway track", category: "travel" },
    { emoji: "⛽", name: "fuel pump", category: "travel" },
    { emoji: "🚨", name: "police car light", category: "travel" },
    { emoji: "🚥", name: "horizontal traffic light", category: "travel" },
    { emoji: "🚦", name: "vertical traffic light", category: "travel" },
    { emoji: "🚧", name: "construction", category: "travel" },
    { emoji: "⚓", name: "anchor", category: "travel" },
    { emoji: "⛵", name: "sailboat", category: "travel" },
    { emoji: "🚤", name: "speedboat", category: "travel" },
    { emoji: "🛳️", name: "passenger ship", category: "travel" },
    { emoji: "⛴️", name: "ferry", category: "travel" },
    { emoji: "🛥️", name: "motor boat", category: "travel" },
    { emoji: "🚢", name: "ship", category: "travel" },
    { emoji: "✈️", name: "airplane", category: "travel" },
    { emoji: "🛩️", name: "small airplane", category: "travel" },
    { emoji: "🛫", name: "airplane departure", category: "travel" },
    { emoji: "🛬", name: "airplane arrival", category: "travel" },
    { emoji: "🪂", name: "parachute", category: "travel" },
    { emoji: "💺", name: "seat", category: "travel" },
    { emoji: "🚁", name: "helicopter", category: "travel" },
    { emoji: "🚟", name: "suspension railway", category: "travel" },
    { emoji: "🚠", name: "mountain cableway", category: "travel" },
    { emoji: "🚡", name: "aerial tramway", category: "travel" },
    { emoji: "🛰️", name: "satellite", category: "travel" },
    { emoji: "🚀", name: "rocket", category: "travel" },
    { emoji: "🛸", name: "flying saucer", category: "travel" },
    { emoji: "🏠", name: "house", category: "travel" },
    { emoji: "🏡", name: "house with garden", category: "travel" },
    { emoji: "🏢", name: "office building", category: "travel" },
    { emoji: "🏣", name: "Japanese post office", category: "travel" },
    { emoji: "🏤", name: "post office", category: "travel" },
    { emoji: "🏥", name: "hospital", category: "travel" },
    { emoji: "🏦", name: "bank", category: "travel" },
    { emoji: "🏨", name: "hotel", category: "travel" },
    { emoji: "🏩", name: "love hotel", category: "travel" },
    { emoji: "🏪", name: "convenience store", category: "travel" },
    { emoji: "🏫", name: "school", category: "travel" },
    { emoji: "🏬", name: "department store", category: "travel" },
    { emoji: "🏭", name: "factory", category: "travel" },
    { emoji: "🏯", name: "Japanese castle", category: "travel" },
    { emoji: "🏰", name: "castle", category: "travel" },
    { emoji: "💒", name: "wedding", category: "travel" },
    { emoji: "🗼", name: "Tokyo tower", category: "travel" },
    { emoji: "🗽", name: "Statue of Liberty", category: "travel" },
    { emoji: "⛪", name: "church", category: "travel" },
    { emoji: "🕌", name: "mosque", category: "travel" },
    { emoji: "🛕", name: "hindu temple", category: "travel" },
    { emoji: "🕍", name: "synagogue", category: "travel" },
    { emoji: "⛩️", name: "shinto shrine", category: "travel" },
    { emoji: "🕋", name: "kaaba", category: "travel" },
    { emoji: "⛲", name: "fountain", category: "travel" },
    { emoji: "⛺", name: "tent", category: "travel" },
    { emoji: "🌁", name: "foggy", category: "travel" },
    { emoji: "🌃", name: "night with stars", category: "travel" },
    { emoji: "🏙️", name: "cityscape", category: "travel" },
    { emoji: "🌄", name: "sunrise over mountains", category: "travel" },
    { emoji: "🌅", name: "sunrise", category: "travel" },
    { emoji: "🌆", name: "cityscape at dusk", category: "travel" },
    { emoji: "🌇", name: "sunset", category: "travel" },
    { emoji: "🌉", name: "bridge at night", category: "travel" },
    { emoji: "🎠", name: "carousel horse", category: "travel" },
    { emoji: "🎡", name: "ferris wheel", category: "travel" },
    { emoji: "🎢", name: "roller coaster", category: "travel" },
  ],
  objects: [
    { emoji: "💡", name: "light bulb", category: "objects" },
    { emoji: "🔦", name: "flashlight", category: "objects" },
    { emoji: "🏮", name: "red paper lantern", category: "objects" },
    { emoji: "🪔", name: "diya lamp", category: "objects" },
    { emoji: "📱", name: "mobile phone", category: "objects" },
    { emoji: "📲", name: "mobile phone with arrow", category: "objects" },
    { emoji: "💻", name: "laptop", category: "objects" },
    { emoji: "🖥️", name: "desktop computer", category: "objects" },
    { emoji: "🖨️", name: "printer", category: "objects" },
    { emoji: "⌨️", name: "keyboard", category: "objects" },
    { emoji: "🖱️", name: "computer mouse", category: "objects" },
    { emoji: "🖲️", name: "trackball", category: "objects" },
    { emoji: "💽", name: "computer disk", category: "objects" },
    { emoji: "💾", name: "floppy disk", category: "objects" },
    { emoji: "💿", name: "optical disk", category: "objects" },
    { emoji: "📀", name: "dvd", category: "objects" },
    { emoji: "🧮", name: "abacus", category: "objects" },
    { emoji: "🎥", name: "movie camera", category: "objects" },
    { emoji: "🎞️", name: "film frames", category: "objects" },
    { emoji: "📽️", name: "film projector", category: "objects" },
    { emoji: "📺", name: "television", category: "objects" },
    { emoji: "📷", name: "camera", category: "objects" },
    { emoji: "📸", name: "camera with flash", category: "objects" },
    { emoji: "📹", name: "video camera", category: "objects" },
    { emoji: "📼", name: "videocassette", category: "objects" },
    { emoji: "🔍", name: "magnifying glass tilted left", category: "objects" },
    { emoji: "🔎", name: "magnifying glass tilted right", category: "objects" },
    { emoji: "🕯️", name: "candle", category: "objects" },
    { emoji: "📔", name: "notebook with decorative cover", category: "objects" },
    { emoji: "📕", name: "closed book", category: "objects" },
    { emoji: "📖", name: "open book", category: "objects" },
    { emoji: "📗", name: "green book", category: "objects" },
    { emoji: "📘", name: "blue book", category: "objects" },
    { emoji: "📙", name: "orange book", category: "objects" },
    { emoji: "📚", name: "books", category: "objects" },
    { emoji: "📓", name: "notebook", category: "objects" },
    { emoji: "📒", name: "ledger", category: "objects" },
    { emoji: "📃", name: "page with curl", category: "objects" },
    { emoji: "📜", name: "scroll", category: "objects" },
    { emoji: "📄", name: "page facing up", category: "objects" },
    { emoji: "📰", name: "newspaper", category: "objects" },
    { emoji: "🗞️", name: "rolled-up newspaper", category: "objects" },
    { emoji: "📑", name: "bookmark tabs", category: "objects" },
    { emoji: "🔖", name: "bookmark", category: "objects" },
    { emoji: "🏷️", name: "label", category: "objects" },
    { emoji: "💰", name: "money bag", category: "objects" },
    { emoji: "🪙", name: "coin", category: "objects" },
    { emoji: "💴", name: "yen banknote", category: "objects" },
    { emoji: "💵", name: "dollar banknote", category: "objects" },
    { emoji: "💶", name: "euro banknote", category: "objects" },
    { emoji: "💷", name: "pound banknote", category: "objects" },
    { emoji: "💸", name: "money with wings", category: "objects" },
    { emoji: "💳", name: "credit card", category: "objects" },
    { emoji: "🧾", name: "receipt", category: "objects" },
    { emoji: "✉️", name: "envelope", category: "objects" },
    { emoji: "📧", name: "e-mail", category: "objects" },
    { emoji: "📨", name: "incoming envelope", category: "objects" },
    { emoji: "📩", name: "envelope with arrow", category: "objects" },
    { emoji: "📤", name: "outbox tray", category: "objects" },
    { emoji: "📥", name: "inbox tray", category: "objects" },
    { emoji: "📦", name: "package", category: "objects" },
    { emoji: "📫", name: "closed mailbox with raised flag", category: "objects" },
    { emoji: "📪", name: "closed mailbox with lowered flag", category: "objects" },
    { emoji: "📬", name: "open mailbox with raised flag", category: "objects" },
    { emoji: "📭", name: "open mailbox with lowered flag", category: "objects" },
    { emoji: "📮", name: "postbox", category: "objects" },
    { emoji: "🗳️", name: "ballot box with ballot", category: "objects" },
    { emoji: "✏️", name: "pencil", category: "objects" },
    { emoji: "✒️", name: "black nib", category: "objects" },
    { emoji: "🖋️", name: "fountain pen", category: "objects" },
    { emoji: "🖊️", name: "pen", category: "objects" },
    { emoji: "🖌️", name: "paintbrush", category: "objects" },
    { emoji: "🖍️", name: "crayon", category: "objects" },
    { emoji: "📝", name: "memo", category: "objects" },
    { emoji: "📁", name: "file folder", category: "objects" },
    { emoji: "📂", name: "open file folder", category: "objects" },
    { emoji: "🗂️", name: "card index dividers", category: "objects" },
    { emoji: "📅", name: "calendar", category: "objects" },
    { emoji: "📆", name: "tear-off calendar", category: "objects" },
    { emoji: "🗒️", name: "spiral notepad", category: "objects" },
    { emoji: "🗓️", name: "spiral calendar", category: "objects" },
    { emoji: "📇", name: "card index", category: "objects" },
    { emoji: "📈", name: "chart increasing", category: "objects" },
    { emoji: "📉", name: "chart decreasing", category: "objects" },
    { emoji: "📊", name: "bar chart", category: "objects" },
    { emoji: "📋", name: "clipboard", category: "objects" },
    { emoji: "📌", name: "pushpin", category: "objects" },
    { emoji: "📍", name: "round pushpin", category: "objects" },
    { emoji: "📎", name: "paperclip", category: "objects" },
    { emoji: "🖇️", name: "linked paperclips", category: "objects" },
    { emoji: "📏", name: "straight ruler", category: "objects" },
    { emoji: "📐", name: "triangular ruler", category: "objects" },
    { emoji: "✂️", name: "scissors", category: "objects" },
    { emoji: "🗃️", name: "card file box", category: "objects" },
    { emoji: "🗄️", name: "file cabinet", category: "objects" },
    { emoji: "🗑️", name: "wastebasket", category: "objects" },
    { emoji: "🔒", name: "locked", category: "objects" },
    { emoji: "🔓", name: "unlocked", category: "objects" },
    { emoji: "🔏", name: "locked with pen", category: "objects" },
    { emoji: "🔐", name: "locked with key", category: "objects" },
    { emoji: "🔑", name: "key", category: "objects" },
    { emoji: "🗝️", name: "old key", category: "objects" },
    { emoji: "🔨", name: "hammer", category: "objects" },
    { emoji: "🪓", name: "axe", category: "objects" },
    { emoji: "⛏️", name: "pick", category: "objects" },
    { emoji: "⚒️", name: "hammer and pick", category: "objects" },
    { emoji: "🛠️", name: "hammer and wrench", category: "objects" },
    { emoji: "🗡️", name: "dagger", category: "objects" },
    { emoji: "⚔️", name: "crossed swords", category: "objects" },
    { emoji: "🔫", name: "pistol", category: "objects" },
    { emoji: "🪃", name: "boomerang", category: "objects" },
    { emoji: "🛡️", name: "shield", category: "objects" },
    { emoji: "🪚", name: "carpentry saw", category: "objects" },
    { emoji: "🔧", name: "wrench", category: "objects" },
    { emoji: "🪛", name: "screwdriver", category: "objects" },
    { emoji: "🔩", name: "nut and bolt", category: "objects" },
    { emoji: "⚙️", name: "gear", category: "objects" },
    { emoji: "🗜️", name: "clamp", category: "objects" },
    { emoji: "⚖️", name: "balance scale", category: "objects" },
    { emoji: "🦯", name: "white cane", category: "objects" },
    { emoji: "🔗", name: "link", category: "objects" },
    { emoji: "⛓️", name: "chains", category: "objects" },
    { emoji: "🪝", name: "hook", category: "objects" },
    { emoji: "🧰", name: "toolbox", category: "objects" },
    { emoji: "🧲", name: "magnet", category: "objects" },
    { emoji: "🪜", name: "ladder", category: "objects" },
  ],
  symbols: [
    { emoji: "❤️", name: "red heart", category: "symbols" },
    { emoji: "🧡", name: "orange heart", category: "symbols" },
    { emoji: "💛", name: "yellow heart", category: "symbols" },
    { emoji: "💚", name: "green heart", category: "symbols" },
    { emoji: "💙", name: "blue heart", category: "symbols" },
    { emoji: "💜", name: "purple heart", category: "symbols" },
    { emoji: "🖤", name: "black heart", category: "symbols" },
    { emoji: "🤍", name: "white heart", category: "symbols" },
    { emoji: "🤎", name: "brown heart", category: "symbols" },
    { emoji: "💔", name: "broken heart", category: "symbols" },
    { emoji: "❣️", name: "heart exclamation", category: "symbols" },
    { emoji: "💕", name: "two hearts", category: "symbols" },
    { emoji: "💞", name: "revolving hearts", category: "symbols" },
    { emoji: "💓", name: "beating heart", category: "symbols" },
    { emoji: "💗", name: "growing heart", category: "symbols" },
    { emoji: "💖", name: "sparkling heart", category: "symbols" },
    { emoji: "💘", name: "heart with arrow", category: "symbols" },
    { emoji: "💝", name: "heart with ribbon", category: "symbols" },
    { emoji: "💟", name: "heart decoration", category: "symbols" },
    { emoji: "☮️", name: "peace symbol", category: "symbols" },
    { emoji: "✝️", name: "latin cross", category: "symbols" },
    { emoji: "☪️", name: "star and crescent", category: "symbols" },
    { emoji: "🕉️", name: "om", category: "symbols" },
    { emoji: "☸️", name: "wheel of dharma", category: "symbols" },
    { emoji: "✡️", name: "star of David", category: "symbols" },
    { emoji: "🔯", name: "dotted six-pointed star", category: "symbols" },
    { emoji: "🕎", name: "menorah", category: "symbols" },
    { emoji: "☯️", name: "yin yang", category: "symbols" },
    { emoji: "☦️", name: "orthodox cross", category: "symbols" },
    { emoji: "🛐", name: "place of worship", category: "symbols" },
    { emoji: "⛎", name: "Ophiuchus", category: "symbols" },
    { emoji: "♈", name: "Aries", category: "symbols" },
    { emoji: "♉", name: "Taurus", category: "symbols" },
    { emoji: "♊", name: "Gemini", category: "symbols" },
    { emoji: "♋", name: "Cancer", category: "symbols" },
    { emoji: "♌", name: "Leo", category: "symbols" },
    { emoji: "♍", name: "Virgo", category: "symbols" },
    { emoji: "♎", name: "Libra", category: "symbols" },
    { emoji: "♏", name: "Scorpio", category: "symbols" },
    { emoji: "♐", name: "Sagittarius", category: "symbols" },
    { emoji: "♑", name: "Capricorn", category: "symbols" },
    { emoji: "♒", name: "Aquarius", category: "symbols" },
    { emoji: "♓", name: "Pisces", category: "symbols" },
    { emoji: "🆔", name: "ID button", category: "symbols" },
    { emoji: "⚛️", name: "atom symbol", category: "symbols" },
    { emoji: "🉑", name: "Japanese acceptable button", category: "symbols" },
    { emoji: "☢️", name: "radioactive", category: "symbols" },
    { emoji: "☣️", name: "biohazard", category: "symbols" },
    { emoji: "📴", name: "mobile phone off", category: "symbols" },
    { emoji: "📳", name: "vibration mode", category: "symbols" },
    { emoji: "🈶", name: "Japanese not free of charge button", category: "symbols" },
    { emoji: "🈚", name: "Japanese free of charge button", category: "symbols" },
    { emoji: "🈸", name: "Japanese application button", category: "symbols" },
    { emoji: "🈺", name: "Japanese open for business button", category: "symbols" },
    { emoji: "🈷️", name: "Japanese monthly amount button", category: "symbols" },
    { emoji: "✴️", name: "eight-pointed star", category: "symbols" },
    { emoji: "🆚", name: "VS button", category: "symbols" },
    { emoji: "💮", name: "white flower", category: "symbols" },
    { emoji: "🉐", name: "Japanese bargain button", category: "symbols" },
    { emoji: "㊙️", name: "Japanese secret button", category: "symbols" },
    { emoji: "㊗️", name: "Japanese congratulations button", category: "symbols" },
    { emoji: "🈴", name: "Japanese passing grade button", category: "symbols" },
    { emoji: "🈵", name: "Japanese no vacancy button", category: "symbols" },
    { emoji: "🈹", name: "Japanese discount button", category: "symbols" },
    { emoji: "🈲", name: "Japanese prohibited button", category: "symbols" },
    { emoji: "🅰️", name: "A button (blood type)", category: "symbols" },
    { emoji: "🅱️", name: "B button (blood type)", category: "symbols" },
    { emoji: "🆎", name: "AB button (blood type)", category: "symbols" },
    { emoji: "🆑", name: "CL button", category: "symbols" },
    { emoji: "🅾️", name: "O button (blood type)", category: "symbols" },
    { emoji: "🆘", name: "SOS button", category: "symbols" },
    { emoji: "❌", name: "cross mark", category: "symbols" },
    { emoji: "⭕", name: "hollow red circle", category: "symbols" },
    { emoji: "🛑", name: "stop sign", category: "symbols" },
    { emoji: "⛔", name: "no entry", category: "symbols" },
    { emoji: "📛", name: "name badge", category: "symbols" },
    { emoji: "🚫", name: "prohibited", category: "symbols" },
    { emoji: "💯", name: "hundred points", category: "symbols" },
    { emoji: "💢", name: "anger symbol", category: "symbols" },
    { emoji: "♨️", name: "hot springs", category: "symbols" },
    { emoji: "🚷", name: "no pedestrians", category: "symbols" },
    { emoji: "🚯", name: "no littering", category: "symbols" },
    { emoji: "🚳", name: "no bicycles", category: "symbols" },
    { emoji: "🚱", name: "non-potable water", category: "symbols" },
    { emoji: "🔞", name: "no one under eighteen", category: "symbols" },
    { emoji: "📵", name: "no mobile phones", category: "symbols" },
    { emoji: "🚭", name: "no smoking", category: "symbols" },
    { emoji: "❗", name: "exclamation mark", category: "symbols" },
    { emoji: "❕", name: "white exclamation mark", category: "symbols" },
    { emoji: "❓", name: "question mark", category: "symbols" },
    { emoji: "❔", name: "white question mark", category: "symbols" },
    { emoji: "‼️", name: "double exclamation mark", category: "symbols" },
    { emoji: "⁉️", name: "exclamation question mark", category: "symbols" },
    { emoji: "🔅", name: "dim button", category: "symbols" },
    { emoji: "🔆", name: "bright button", category: "symbols" },
    { emoji: "〽️", name: "part alternation mark", category: "symbols" },
    { emoji: "⚠️", name: "warning", category: "symbols" },
    { emoji: "🚸", name: "children crossing", category: "symbols" },
    { emoji: "🔱", name: "trident emblem", category: "symbols" },
    { emoji: "⚜️", name: "fleur-de-lis", category: "symbols" },
    { emoji: "🔰", name: "Japanese symbol for beginner", category: "symbols" },
    { emoji: "♻️", name: "recycling symbol", category: "symbols" },
    { emoji: "✅", name: "check mark button", category: "symbols" },
    { emoji: "🈯", name: "Japanese reserved button", category: "symbols" },
    { emoji: "💹", name: "chart increasing with yen", category: "symbols" },
    { emoji: "❇️", name: "sparkle", category: "symbols" },
    { emoji: "✳️", name: "eight-spoked asterisk", category: "symbols" },
    { emoji: "❎", name: "cross mark button", category: "symbols" },
    { emoji: "🌐", name: "globe with meridians", category: "symbols" },
    { emoji: "💠", name: "diamond with a dot", category: "symbols" },
    { emoji: "Ⓜ️", name: "circled M", category: "symbols" },
    { emoji: "🌀", name: "cyclone", category: "symbols" },
    { emoji: "💤", name: "zzz", category: "symbols" },
    { emoji: "🏧", name: "ATM sign", category: "symbols" },
    { emoji: "🚾", name: "water closet", category: "symbols" },
    { emoji: "♿", name: "wheelchair symbol", category: "symbols" },
    { emoji: "🅿️", name: "P button", category: "symbols" },
    { emoji: "🛗", name: "elevator", category: "symbols" },
    { emoji: "🈳", name: "Japanese vacancy button", category: "symbols" },
    { emoji: "🈂️", name: "Japanese service charge button", category: "symbols" },
    { emoji: "🛂", name: "passport control", category: "symbols" },
    { emoji: "🛃", name: "customs", category: "symbols" },
    { emoji: "🛄", name: "baggage claim", category: "symbols" },
    { emoji: "🛅", name: "left luggage", category: "symbols" },
    { emoji: "🚹", name: "men's room", category: "symbols" },
    { emoji: "🚺", name: "women's room", category: "symbols" },
    { emoji: "🚼", name: "baby symbol", category: "symbols" },
    { emoji: "⚧️", name: "transgender symbol", category: "symbols" },
    { emoji: "🚻", name: "restroom", category: "symbols" },
    { emoji: "🚮", name: "litter in bin sign", category: "symbols" },
    { emoji: "🎦", name: "cinema", category: "symbols" },
    { emoji: "📶", name: "antenna bars", category: "symbols" },
    { emoji: "🈁", name: "Japanese here button", category: "symbols" },
    { emoji: "🔣", name: "input symbols", category: "symbols" },
    { emoji: "ℹ️", name: "information", category: "symbols" },
    { emoji: "🔤", name: "input latin letters", category: "symbols" },
    { emoji: "🔡", name: "input latin lowercase", category: "symbols" },
    { emoji: "🔠", name: "input latin uppercase", category: "symbols" },
    { emoji: "🆖", name: "NG button", category: "symbols" },
    { emoji: "🆗", name: "OK button", category: "symbols" },
    { emoji: "🆙", name: "UP! button", category: "symbols" },
    { emoji: "🆒", name: "COOL button", category: "symbols" },
    { emoji: "🆕", name: "NEW button", category: "symbols" },
    { emoji: "🆓", name: "FREE button", category: "symbols" },
    { emoji: "0️⃣", name: "keycap: 0", category: "symbols" },
    { emoji: "1️⃣", name: "keycap: 1", category: "symbols" },
    { emoji: "2️⃣", name: "keycap: 2", category: "symbols" },
    { emoji: "3️⃣", name: "keycap: 3", category: "symbols" },
    { emoji: "4️⃣", name: "keycap: 4", category: "symbols" },
    { emoji: "5️⃣", name: "keycap: 5", category: "symbols" },
    { emoji: "6️⃣", name: "keycap: 6", category: "symbols" },
    { emoji: "7️⃣", name: "keycap: 7", category: "symbols" },
    { emoji: "8️⃣", name: "keycap: 8", category: "symbols" },
    { emoji: "9️⃣", name: "keycap: 9", category: "symbols" },
    { emoji: "🔟", name: "keycap: 10", category: "symbols" },
    { emoji: "🔢", name: "input numbers", category: "symbols" },
    { emoji: "#️⃣", name: "keycap: #", category: "symbols" },
    { emoji: "*️⃣", name: "keycap: *", category: "symbols" },
    { emoji: "⏏️", name: "eject button", category: "symbols" },
    { emoji: "▶️", name: "play button", category: "symbols" },
    { emoji: "⏸️", name: "pause button", category: "symbols" },
    { emoji: "⏯️", name: "play or pause button", category: "symbols" },
    { emoji: "⏹️", name: "stop button", category: "symbols" },
    { emoji: "⏺️", name: "record button", category: "symbols" },
    { emoji: "⏭️", name: "next track button", category: "symbols" },
    { emoji: "⏮️", name: "last track button", category: "symbols" },
    { emoji: "⏩", name: "fast-forward button", category: "symbols" },
    { emoji: "⏪", name: "fast reverse button", category: "symbols" },
    { emoji: "⏫", name: "fast up button", category: "symbols" },
    { emoji: "⏬", name: "fast down button", category: "symbols" },
    { emoji: "◀️", name: "reverse button", category: "symbols" },
    { emoji: "🔼", name: "upwards button", category: "symbols" },
    { emoji: "🔽", name: "downwards button", category: "symbols" },
    { emoji: "➡️", name: "right arrow", category: "symbols" },
    { emoji: "⬅️", name: "left arrow", category: "symbols" },
    { emoji: "⬆️", name: "up arrow", category: "symbols" },
    { emoji: "⬇️", name: "down arrow", category: "symbols" },
    { emoji: "↗️", name: "up-right arrow", category: "symbols" },
    { emoji: "↘️", name: "down-right arrow", category: "symbols" },
    { emoji: "↙️", name: "down-left arrow", category: "symbols" },
    { emoji: "↖️", name: "up-left arrow", category: "symbols" },
    { emoji: "↕️", name: "up-down arrow", category: "symbols" },
    { emoji: "↔️", name: "left-right arrow", category: "symbols" },
    { emoji: "↪️", name: "right arrow curving left", category: "symbols" },
    { emoji: "↩️", name: "left arrow curving right", category: "symbols" },
    { emoji: "⤴️", name: "right arrow curving up", category: "symbols" },
    { emoji: "⤵️", name: "right arrow curving down", category: "symbols" },
    { emoji: "🔀", name: "shuffle tracks button", category: "symbols" },
    { emoji: "🔁", name: "repeat button", category: "symbols" },
    { emoji: "🔂", name: "repeat single button", category: "symbols" },
    { emoji: "🔄", name: "counterclockwise arrows button", category: "symbols" },
    { emoji: "🔃", name: "clockwise vertical arrows", category: "symbols" },
    { emoji: "🎵", name: "musical note", category: "symbols" },
    { emoji: "🎶", name: "musical notes", category: "symbols" },
    { emoji: "➕", name: "plus", category: "symbols" },
    { emoji: "➖", name: "minus", category: "symbols" },
    { emoji: "➗", name: "divide", category: "symbols" },
    { emoji: "✖️", name: "multiply", category: "symbols" },
    { emoji: "♾️", name: "infinity", category: "symbols" },
    { emoji: "💲", name: "heavy dollar sign", category: "symbols" },
    { emoji: "💱", name: "currency exchange", category: "symbols" },
    { emoji: "™️", name: "trade mark", category: "symbols" },
    { emoji: "©️", name: "copyright", category: "symbols" },
    { emoji: "®️", name: "registered", category: "symbols" },
    { emoji: "〰️", name: "wavy dash", category: "symbols" },
    { emoji: "➰", name: "curly loop", category: "symbols" },
    { emoji: "➿", name: "double curly loop", category: "symbols" },
    { emoji: "🔚", name: "END arrow", category: "symbols" },
    { emoji: "🔙", name: "BACK arrow", category: "symbols" },
    { emoji: "🔛", name: "ON! arrow", category: "symbols" },
    { emoji: "🔝", name: "TOP arrow", category: "symbols" },
    { emoji: "🔜", name: "SOON arrow", category: "symbols" },
    { emoji: "✔️", name: "check mark", category: "symbols" },
    { emoji: "☑️", name: "check box with check", category: "symbols" },
    { emoji: "🔘", name: "radio button", category: "symbols" },
    { emoji: "🔴", name: "red circle", category: "symbols" },
    { emoji: "🟠", name: "orange circle", category: "symbols" },
    { emoji: "🟡", name: "yellow circle", category: "symbols" },
    { emoji: "🟢", name: "green circle", category: "symbols" },
    { emoji: "🔵", name: "blue circle", category: "symbols" },
    { emoji: "🟣", name: "purple circle", category: "symbols" },
    { emoji: "🟤", name: "brown circle", category: "symbols" },
    { emoji: "⚫", name: "black circle", category: "symbols" },
    { emoji: "⚪", name: "white circle", category: "symbols" },
    { emoji: "🟥", name: "red square", category: "symbols" },
    { emoji: "🟧", name: "orange square", category: "symbols" },
    { emoji: "🟨", name: "yellow square", category: "symbols" },
    { emoji: "🟩", name: "green square", category: "symbols" },
    { emoji: "🟦", name: "blue square", category: "symbols" },
    { emoji: "🟪", name: "purple square", category: "symbols" },
    { emoji: "🟫", name: "brown square", category: "symbols" },
    { emoji: "⬛", name: "black large square", category: "symbols" },
    { emoji: "⬜", name: "white large square", category: "symbols" },
    { emoji: "◼️", name: "black medium square", category: "symbols" },
    { emoji: "◻️", name: "white medium square", category: "symbols" },
    { emoji: "◾", name: "black medium-small square", category: "symbols" },
    { emoji: "◽", name: "white medium-small square", category: "symbols" },
    { emoji: "▪️", name: "black small square", category: "symbols" },
    { emoji: "▫️", name: "white small square", category: "symbols" },
    { emoji: "🔶", name: "large orange diamond", category: "symbols" },
    { emoji: "🔷", name: "large blue diamond", category: "symbols" },
    { emoji: "🔸", name: "small orange diamond", category: "symbols" },
    { emoji: "🔹", name: "small blue diamond", category: "symbols" },
    { emoji: "🔺", name: "red triangle pointed up", category: "symbols" },
    { emoji: "🔻", name: "red triangle pointed down", category: "symbols" },
    { emoji: "💠", name: "diamond with a dot", category: "symbols" },
    { emoji: "🔳", name: "white square button", category: "symbols" },
    { emoji: "🔲", name: "black square button", category: "symbols" },
  ],
};

// All emojis flat list for search
const allEmojis = computed(() => {
  return Object.values(emojiData).flat();
});

// Filtered emojis based on search or category
const filteredEmojis = computed(() => {
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    return allEmojis.value.filter((emoji) =>
      emoji.name.toLowerCase().includes(query)
    );
  }
  return emojiData[activeCategory.value] || [];
});

// Popup positioning
const popupStyle = computed((): CSSProperties => {
  if (!triggerRect.value) return {};
  const rect = triggerRect.value;
  const popupWidth = 320;
  const popupHeight = 350;
  const padding = 8;

  // Calculate position (prefer above, fallback to below)
  let top = rect.top - popupHeight - padding;
  let left = rect.left + rect.width / 2 - popupWidth / 2;

  // Ensure within viewport
  if (top < padding) {
    top = rect.bottom + padding;
  }
  if (left < padding) {
    left = padding;
  }
  if (left + popupWidth > window.innerWidth - padding) {
    left = window.innerWidth - popupWidth - padding;
  }

  return {
    position: "fixed" as const,
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 9999,
  };
});

// Methods
function togglePicker() {
  if (isOpen.value) {
    closePicker();
  } else {
    openPicker();
  }
}

function openPicker() {
  if (triggerRef.value) {
    triggerRect.value = triggerRef.value.getBoundingClientRect();
  }
  isOpen.value = true;
  searchQuery.value = "";
}

function closePicker() {
  isOpen.value = false;
}

function selectEmoji(emoji: string) {
  emit("select", emoji);
  closePicker();
}

function handleSearch() {
  // Search is reactive via v-model
}

// Close on outside click
function handleOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (
    !target.closest(".emoji-picker") &&
    !target.closest(".emoji-picker__popup")
  ) {
    closePicker();
  }
}

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
});
</script>

<style scoped lang="scss">
.emoji-picker {
  position: relative;
  display: inline-flex;

  &__trigger {
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 50%;
    color: #f59e0b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(245, 158, 11, 0.1);
    }

    &:focus-visible {
      outline: 2px solid #f59e0b;
      outline-offset: 2px;
    }
  }

  &__popup {
    width: 320px;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
    border-radius: 16px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
    overflow: hidden;

    // Theme CSS variables for teleported content
    &.v-theme--dark {
      --v-theme-surface: 28, 25, 23;
      --v-theme-on-surface: 250, 250, 249;
    }

    &.v-theme--light {
      --v-theme-surface: 255, 255, 255;
      --v-theme-on-surface: 28, 25, 23;
    }
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  &__search-icon {
    color: rgba(var(--v-theme-on-surface), 0.5);
    flex-shrink: 0;
  }

  &__search-input {
    flex: 1;
    border: none;
    background: transparent;
    color: rgb(var(--v-theme-on-surface));
    font-size: 0.875rem;
    outline: none;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.4);
    }
  }

  &__categories {
    display: flex;
    gap: 2px;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    overflow-x: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__category-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
    }

    &--active {
      background: rgba(245, 158, 11, 0.2);
    }
  }

  &__grid-container {
    height: 250px;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-on-surface), 0.2);
      border-radius: 3px;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 4px;
  }

  &__emoji {
    width: 100%;
    aspect-ratio: 1;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      transform: scale(1.15);
    }
  }

  &__empty {
    text-align: center;
    padding: 24px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }
}

// Popup transition
.emoji-picker-pop-enter-active,
.emoji-picker-pop-leave-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.emoji-picker-pop-enter-from,
.emoji-picker-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(8px);
}

// Light theme adjustments
.v-theme--light {
  .emoji-picker {
    &__trigger {
      color: #d97706;

      &:hover {
        background: rgba(217, 119, 6, 0.1);
      }
    }

    &__popup {
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
    }

    &__category-btn--active {
      background: rgba(217, 119, 6, 0.15);
    }
  }
}
</style>
